import type { EntregaFormState } from '../../domain/EntregaFormState.ts';
import { entregaFormStateForPunto } from '../../domain/entregaMapTileMath.ts';
import type { EntregaCatalogRepositoryPort } from '../../domain/ports/EntregaPorts.ts';
import { prefetchEntregaMapOsmTiles } from './entregaMapTileCache.ts';

const PUNTO_WARMUP_CONCURRENCY = 4;

export type EntregaMapWarmupOpts = {
  onProgress?: (processed: number, total: number) => void;
};

async function prefetchPuntosWithConcurrency(
  states: readonly EntregaFormState[],
  onProgress?: (processed: number, total: number) => void,
): Promise<void> {
  if (states.length === 0) {
    onProgress?.(0, 0);
    return;
  }
  let next = 0;
  let processed = 0;
  const total = states.length;
  const workers = Array.from({ length: Math.min(PUNTO_WARMUP_CONCURRENCY, states.length) }, async () => {
    while (next < states.length) {
      const state = states[next++]!;
      await prefetchEntregaMapOsmTiles(state);
      processed += 1;
      onProgress?.(processed, total);
    }
  });
  await Promise.all(workers);
}

/** Prefetch OSM tiles for every catalog delivery point with coordinates. */
export async function warmupEntregaMapTilesFromCatalog(
  catalog: EntregaCatalogRepositoryPort,
  opts?: EntregaMapWarmupOpts,
): Promise<void> {
  if (globalThis.navigator?.onLine === false) return;
  const core = await catalog.loadFormCatalogCore();
  const states: EntregaFormState[] = [];
  for (const comuna of core.comunas) {
    const puntos = await catalog.loadPuntosByComuna(comuna.publicId);
    for (const punto of puntos.filter((p) => p.lat != null && p.lng != null)) {
      states.push(
        entregaFormStateForPunto({
          comunaPublicId: punto.comunaPublicId,
          publicId: punto.publicId,
          direccion: punto.direccion,
          lat: punto.lat!,
          lng: punto.lng!,
        }),
      );
    }
  }
  await prefetchPuntosWithConcurrency(states, opts?.onProgress);
}

/** @internal test hook */
export function entregaMapWarmupConcurrency(): { puntos: number } {
  return { puntos: PUNTO_WARMUP_CONCURRENCY };
}
